/*
 * Copyright 2017 Palantir Technologies, Inc. All rights reserved.
 * Licensed under the BSD-3 License as modified (the “License”); you may obtain a copy
 * of the license at https://github.com/palantir/blueprint/blob/master/LICENSE
 * and https://github.com/palantir/blueprint/blob/master/PATENTS
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var moment = require("moment-timezone");
var timezoneMetadata_1 = require("./timezoneMetadata");
var timezoneUtils_1 = require("./timezoneUtils");
/**
 * Get a list of all timezone items.
 * @param date the date to use when determining timezone offsets
 */
function getTimezoneItems(date) {
    return moment.tz.names().map(function (timezone) { return toTimezoneItem(timezone, date); });
}
exports.getTimezoneItems = getTimezoneItems;
/**
 * Get a list of timezone items where there is one timezone per offset
 * and optionally the local timezone as the first item.
 * The most populous timezone for each offset is chosen.
 * @param date the date to use when determining timezone offsets
 * @param includeLocalTimezone whether to include the local timezone
 */
function getInitialTimezoneItems(date, includeLocalTimezone) {
    var populous = getPopulousTimezoneItems(date);
    var local = getLocalTimezoneItem(date);
    return includeLocalTimezone && local !== undefined ? [local].concat(populous) : populous;
}
exports.getInitialTimezoneItems = getInitialTimezoneItems;
/**
 * Get the timezone item for the user's local timezone.
 * @param date the date to use when determining timezone offsets
 */
function getLocalTimezoneItem(date) {
    var timezone = timezoneUtils_1.getLocalTimezone();
    if (timezone !== undefined) {
        var timestamp = date.getTime();
        var zonedDate = moment.tz(timestamp, timezone);
        var offsetAsString = zonedDate.format("Z");
        return {
            iconName: "locate",
            key: timezone + "-local",
            label: offsetAsString,
            text: "Current timezone",
            timezone: timezone,
        };
    }
    else {
        return undefined;
    }
}
exports.getLocalTimezoneItem = getLocalTimezoneItem;
/**
 * Get one timezone item per offset, using the most populous region when there is more
 * than one region for the offset.
 * @param date the date to use when determining timezone offsets
 */
function getPopulousTimezoneItems(date) {
    // Filter out noisy timezones. See https://github.com/moment/moment-timezone/issues/227
    var timezones = moment.tz.names().filter(function (timezone) { return /\//.test(timezone) && !/Etc\//.test(timezone); });
    var timezoneToMetadata = {};
    for (var _i = 0, timezones_1 = timezones; _i < timezones_1.length; _i++) {
        var timezone = timezones_1[_i];
        timezoneToMetadata[timezone] = timezoneMetadata_1.getTimezoneMetadata(timezone, date);
    }
    // Order by offset ascending, population descending, timezone name ascending
    timezones.sort(function (timezone1, timezone2) {
        var _a = timezoneToMetadata[timezone1], offset1 = _a.offset, population1 = _a.population;
        var _b = timezoneToMetadata[timezone2], offset2 = _b.offset, population2 = _b.population;
        if (offset1 === offset2) {
            if (population1 === population2) {
                // Fall back to sorting alphabetically
                return timezone1 < timezone2 ? -1 : 1;
            }
            // Descending order of population
            return population2 - population1;
        }
        // Ascending order of offset
        return offset1 - offset2;
    });
    // Choose the most populous locations for each offset
    var initialTimezones = [];
    var prevOffset;
    for (var _a = 0, timezones_2 = timezones; _a < timezones_2.length; _a++) {
        var timezone = timezones_2[_a];
        var curOffset = timezoneToMetadata[timezone].offset;
        if (prevOffset === undefined || prevOffset !== curOffset) {
            initialTimezones.push(toTimezoneItem(timezone, date));
            prevOffset = curOffset;
        }
    }
    return initialTimezones;
}
function toTimezoneItem(timezone, date) {
    var _a = timezoneMetadata_1.getTimezoneMetadata(timezone, date), abbreviation = _a.abbreviation, offsetAsString = _a.offsetAsString;
    return {
        key: timezone,
        label: offsetAsString,
        text: timezone + (abbreviation ? " (" + abbreviation + ")" : ""),
        timezone: timezone,
    };
}

//# sourceMappingURL=timezoneItems.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/@blueprintjs/labs/dist/components/timezone-picker/timezoneItems.js
// module id = 890
// module chunks = 0